import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { HoldsCountInput } from './HoldsCountInput';
import { LeadTimeInput } from './LeadTimeInput';
import { LeadStyleSwitch } from './LeadStyleSwitch';
import type { LeadClimbStyle } from './lead-climb-style';
import { cn } from '../../lib/utils';

/**
 * A lead card as it is edited: the hold reached, the time on the route, and —
 * where the class may do either — whether it was led or top roped.
 */
export interface LeadCardDraft {
  /** Human format: "17+", "17", or empty while nothing has been entered. */
  holds: string;
  /** Seconds, thousandths in the decimals. Null while nothing has been entered. */
  timeSeconds: number | null;
  /**
   * Top rope or lead. Null while nobody has chosen on a class that may do
   * either, which is a card that cannot be saved — there is no default between
   * two things worth different amounts.
   */
  style?: LeadClimbStyle | null;
}

interface LeadCardEditorProps {
  /** The card as it is saved. Both null on a route nobody has climbed yet. */
  holds: string | null;
  timeSeconds: number | null;
  /** The style on the saved card, where there is one. */
  climbedAs?: LeadClimbStyle | null;
  /**
   * The styles this climber may climb this route in (`permittedLeadStyles`).
   * One of them is a label rather than a choice; two put the switch on the card
   * and hold the save back until one is pressed. Defaults to lead alone, which
   * is what every lead round was before the classes could be put on top rope.
   */
  styles?: LeadClimbStyle[];
  /** The holds on the route: the ceiling, and what the field counts against. */
  maxHolds?: number | null;
  /** Offer the clock — the judge at the wall, not the desk correcting a sheet. */
  stopwatch?: boolean;
  disabled?: boolean;
  saving?: boolean;
  onSave: (draft: LeadCardDraft) => void | Promise<void>;
  /** Takes the card off the board. Absent where there is nothing to take off. */
  onClear?: () => void | Promise<void>;
  className?: string;
  'data-testid'?: string;
}

/**
 * The whole of a lead card, and the whole of writing one: how far up, how long,
 * save.
 *
 * **Both numbers or no card.** The time is the only thing that separates two
 * climbers on the same hold, so a card without one would be unrankable — which is
 * why saving waits for both and why clearing the holds is how a card comes off
 * rather than a half-filled row being accepted (see `LeadRoundResult`).
 *
 * **And the style, where there is one to choose.** A class that may do either
 * top ropes or leads, and a led route beats a top roped one however far up each
 * climber got, so the save waits for that too — a card born with a default would
 * be a place nobody gave it. A class that climbs one way only shows which and
 * asks nothing.
 *
 * A draft until saved, like every other card dialog here: typing and then walking
 * away decides nothing. It is reset from the saved card whenever that changes, so
 * reopening a row never shows what somebody typed and abandoned a minute ago.
 */
export function LeadCardEditor({
  holds,
  timeSeconds,
  climbedAs = null,
  styles = ['lead'],
  maxHolds,
  stopwatch = false,
  disabled = false,
  saving = false,
  onSave,
  onClear,
  className,
  'data-testid': testId,
}: LeadCardEditorProps) {
  const { t } = useTranslation();

  // A class with one style open has nothing to choose, so the draft is born
  // carrying it: the switch is a label, and the card is complete without a tap.
  const onlyStyle = styles.length === 1 ? styles[0] : null;
  const saved: LeadCardDraft = {
    holds: holds ?? '',
    timeSeconds: timeSeconds ?? null,
    style: climbedAs ?? onlyStyle,
  };
  const [draft, setDraft] = useState<LeadCardDraft>(saved);

  // Resynced while rendering rather than in an effect, so the fields never show
  // a stale card for a frame after one is saved somewhere else.
  const [lastSaved, setLastSaved] = useState(saved);
  if (
    lastSaved.holds !== saved.holds ||
    lastSaved.timeSeconds !== saved.timeSeconds ||
    lastSaved.style !== saved.style
  ) {
    setLastSaved(saved);
    setDraft(saved);
  }

  // The style counts towards a complete card exactly where it is a question:
  // nothing here guesses between top rope and lead.
  const complete = draft.holds !== '' && draft.timeSeconds !== null && draft.style != null;
  const changed =
    draft.holds !== saved.holds ||
    draft.timeSeconds !== saved.timeSeconds ||
    draft.style !== saved.style;

  return (
    <div className={cn('flex flex-wrap items-end gap-2', className)} data-testid={testId}>
      <div className="space-y-1">
        <span className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('leadRounds.holds')}
        </span>
        <HoldsCountInput
          value={draft.holds}
          max={maxHolds}
          disabled={disabled}
          onChange={(value) => setDraft((current) => ({ ...current, holds: value }))}
          className="w-28"
          data-testid="lead-holds"
        />
      </div>

      <div className="space-y-1">
        <span className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('leadRounds.time')}
        </span>
        <LeadTimeInput
          value={draft.timeSeconds}
          stopwatch={stopwatch}
          disabled={disabled}
          onChange={(value) => setDraft((current) => ({ ...current, timeSeconds: value }))}
          className={stopwatch ? 'w-48' : 'w-36'}
          data-testid="lead-time"
        />
      </div>

      {/* Top rope or lead. A class that climbs one way only shows which; one
          that may do either has to be told, and the save waits for it. */}
      <div className="space-y-1">
        <span className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('leadRounds.style.label')}
        </span>
        <LeadStyleSwitch
          styles={styles}
          value={draft.style ?? null}
          disabled={disabled}
          onChange={(style) => setDraft((current) => ({ ...current, style }))}
          data-testid="lead-style"
        />
      </div>

      <div className="flex items-center gap-1">
        <Button
          size="sm"
          className="h-10 gap-1.5"
          // Nothing to save on an untouched row, and nothing savable on a
          // half-filled one: the time is what breaks a tie.
          disabled={disabled || saving || !complete || !changed}
          onClick={() => onSave(draft)}
          data-testid="lead-save"
        >
          {saving ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Check className="size-4" aria-hidden />
          )}
          {t('common.save')}
        </Button>

        {onClear && holds !== null && (
          <Button
            size="sm"
            variant="ghost"
            className="h-10 w-10 p-0 text-muted-foreground hover:text-destructive"
            aria-label={t('leadRounds.clearCard')}
            title={t('leadRounds.clearCard')}
            disabled={disabled || saving}
            onClick={() => onClear()}
            data-testid="lead-clear"
          >
            <Trash2 className="size-4" aria-hidden />
          </Button>
        )}
      </div>
    </div>
  );
}
